import { useLoaderData, useLocation } from "@remix-run/react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect, useState } from "react";
import { initPostHogOnce, isPostHogReady } from "./utils/posthogClient";
import { useShopDataStore } from "./store/shopDataStore";

export function PHProvider({ children }: { children: React.ReactNode }) {
  const { myShopifyDomain, isDevEnv, ENV } = useLoaderData<{
    myShopifyDomain: string;
    isDevEnv: boolean;
    ENV: { POSTHOG_API_KEY?: string; POSTHOG_HOST?: string };
  }>();
  const location = useLocation();
  const {
    sessionUsers,
    currSessionUserId,
    planName,
    aiod_plan,
    ownerEmail,
    isEarlyUser,
  } = useShopDataStore();

  const currentUser = sessionUsers.find(
    (user) => user.id === currSessionUserId,
  );

  const [ready, setReady] = useState(() => isPostHogReady());

  // Init lives in an effect, which is what guarantees it only ever runs on the client: it reaches
  // for cookies and localStorage, and an embedded Shopify iframe can have those partitioned or
  // blocked, so a throw must not be able to reach the render pass.
  //
  // React runs child effects before parent effects, so this still lands *after* a route's
  // mount-time `track()`. That is fine and deliberate: `capturePostHog` buffers anything captured
  // before the client is live and `initPostHogOnce` replays it here. Idempotent, so re-running on
  // navigation is a no-op.
  useEffect(() => {
    setReady(
      initPostHogOnce({
        apiKey: ENV?.POSTHOG_API_KEY,
        host: ENV?.POSTHOG_HOST,
        isDevEnv,
        shopDomain: myShopifyDomain,
        pathname: location.pathname,
        search: location.search,
      }),
    );
  }, [
    ENV?.POSTHOG_API_KEY,
    ENV?.POSTHOG_HOST,
    isDevEnv,
    myShopifyDomain,
    location.pathname,
    location.search,
  ]);

  // Identify user when shop data is available
  useEffect(() => {
    if (!ready || !myShopifyDomain || isDevEnv) return;

    // Always use myShopifyDomain as person_id for consistent tracking
    posthog.identify(myShopifyDomain, {
      shop_domain: myShopifyDomain,
      shopify_plan: planName,
      aiod_plan: aiod_plan,
      owner_email: ownerEmail,
      is_early_user: isEarlyUser,
      user_name: currentUser?.name,
      user_email: currentUser?.email,
      is_owner: currentUser?.isOwner,
      is_collaborator: currentUser?.isCollaborator,
    });
  }, [
    ready,
    isDevEnv,
    myShopifyDomain,
    currentUser,
    planName,
    aiod_plan,
    ownerEmail,
    isEarlyUser,
  ]);

  // Track page views on route changes
  useEffect(() => {
    if (!ready || isDevEnv) return;

    const customUrl = `https://${myShopifyDomain}${location.pathname}${location.search}`;

    // Update registered properties for consistent URL override
    posthog.register({
      $current_url: customUrl,
      $host: myShopifyDomain,
      $pathname: location.pathname,
    });

    const pageviewData = {
      $current_url: customUrl,
      $host: myShopifyDomain,
      $pathname: location.pathname,
      route: location.pathname,
    };

    posthog.capture("$pageview", pageviewData);
  }, [ready, isDevEnv, location, myShopifyDomain]);

  // Keep the app mounted while analytics initializes so background user
  // collection and any page interactions already in progress are preserved.
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
